import { Controller } from "@hotwired/stimulus";
import Swiper from "swiper";
/* eslint-disable import/no-unresolved -- Swiper subpath exports are unresolved by eslint */
import {
  A11y,
  Autoplay,
  EffectFade,
  Keyboard,
  Pagination,
} from "swiper/modules";
import type { A11yOptions, KeyboardOptions, SwiperOptions } from "swiper/types";
/* eslint-enable import/no-unresolved */

const MODULES_MAP = {
  A11y,
  Autoplay,
  EffectFade,
  Keyboard,
  Pagination,
} as const;

const DEFAULT_A11Y: A11yOptions = {
  enabled: true,
  containerRoleDescriptionMessage: "carousel",
  itemRoleDescriptionMessage: "slide",
  slideLabelMessage: "Slide {{index}} of {{slidesLength}}",
  paginationBulletMessage: "Go to slide {{index}}",
};

const DEFAULT_KEYBOARD: KeyboardOptions = {
  enabled: true,
  onlyInViewport: true,
};

type Module = keyof typeof MODULES_MAP;

type CustomOptions = {
  play_pause?: boolean;
  play_pause_label?: string;
};

type UserConfig = Partial<
  Omit<SwiperOptions, "modules"> & {
    modules?: Module[];
  }
>;

export default class extends Controller<HTMLElement> {
  static targets = ["carousel", "playPause", "playPauseText", "play", "pause"];

  static values = {
    options: Object,
    customOptions: Object,
    pauseLabel: { type: String, default: "Pause" },
    playLabel: { type: String, default: "Play" },
  };

  private readonly HIDDEN_CLASS = "tw-hidden";

  declare carouselTarget: HTMLElement;
  declare readonly playPauseTarget: HTMLButtonElement;
  declare readonly hasPlayPauseTarget: boolean;
  declare readonly playPauseTextTarget: HTMLElement;
  declare readonly hasPlayPauseTextTarget: boolean;
  declare readonly playTarget: SVGElement;
  declare readonly hasPlayTarget: boolean;
  declare readonly pauseTarget: SVGElement;
  declare readonly hasPauseTarget: boolean;
  declare swiper: Swiper;
  declare optionsValue: UserConfig;
  declare customOptionsValue: CustomOptions;
  declare pauseLabelValue: string;
  declare playLabelValue: string;

  connect(): void {
    try {
      this.init();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Swiper config error:", error);
    }
  }

  init() {
    const prefersReducedMotion = this.prefersReducedMotion();
    const config = this.buildConfig(this.optionsValue, prefersReducedMotion);
    this.swiper = new Swiper(this.carouselTarget, config);

    if (prefersReducedMotion) {
      this.removePlayPauseButton();
    }
  }

  disconnect(): void {
    this.swiper?.destroy(true, true);
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  private removePlayPauseButton(): void {
    if (this.hasPlayPauseTarget) {
      this.playPauseTarget.remove();
    }
  }

  private buildConfig(
    userOptions: UserConfig,
    prefersReducedMotion: boolean
  ): SwiperOptions {
    const {
      modules: userModules,
      autoplay,
      a11y,
      keyboard,
      ...rest
    } = userOptions;

    const moduleNames = new Set<Module>(userModules ?? []);
    moduleNames.add("A11y");
    moduleNames.add("Keyboard");

    if (prefersReducedMotion) {
      moduleNames.delete("Autoplay");
    }

    const modules = Array.from(moduleNames).map((name) => {
      const module = MODULES_MAP[name];
      if (!module) {
        throw new Error(`Unsupported Swiper module: "${name}"`);
      }
      return module;
    });

    return {
      ...rest,
      modules,
      ...(prefersReducedMotion ? {} : { autoplay }),
      a11y: {
        ...DEFAULT_A11Y,
        ...(typeof a11y === "object" ? a11y : {}),
      },
      keyboard: {
        ...DEFAULT_KEYBOARD,
        ...(typeof keyboard === "object" ? keyboard : {}),
      },
    };
  }

  togglePlayPause() {
    if (!this.swiper?.autoplay) {
      return;
    }

    const isRunning = this.swiper.autoplay.running;
    const accessibleName = isRunning
      ? this.playLabelValue
      : this.pauseLabelValue;

    if (this.hasPlayPauseTextTarget) {
      this.playPauseTextTarget.textContent = accessibleName;
    }

    if (this.hasPlayTarget) {
      this.playTarget.classList.toggle(this.HIDDEN_CLASS, !isRunning);
    }

    if (this.hasPauseTarget) {
      this.pauseTarget.classList.toggle(this.HIDDEN_CLASS, isRunning);
    }

    if (this.hasPlayPauseTarget) {
      this.playPauseTarget.setAttribute("aria-pressed", String(isRunning));
      this.playPauseTarget.setAttribute("aria-label", accessibleName);
    }

    isRunning ? this.swiper.autoplay.stop() : this.swiper.autoplay.start();
  }
}
